// @ts-nocheck
// Pure configuration engine (no DOM, no Shopify).
(() => {
  window.Cfg = window.Cfg || {};
  
  // Translation helper
  function t(key, ...args) {
    const translations = window.CfgTranslations && window.CfgTranslations.strings;
    if (!translations || !translations[key]) return key;
    let str = translations[key];
    // Replace {0}, {1}, etc. with arguments
    args.forEach((arg, i) => {
      str = str.replace(new RegExp(`\\{${i}\\}`, 'g'), String(arg));
    });
    return str;
  }

  const Engine = {
    // Centralized defaults used by BOTH preview + UI prefills.
    // These match the fallback lengths in getVerticesForShape.
    getDefaultSideLengthIn(shape, label) {
      switch (shape) {
        case "straight":
          return 120;
        case "l_shape":
          if (label === "A") return 60;
          if (label === "B") return 120;
          return 120;
        case "u_shape":
          if (label === "A") return 60;
          if (label === "B") return 120;
          if (label === "C") return 60;
          return 120;
        case "square":
          if (label === "A") return 60;
          if (label === "B") return 120;
          if (label === "C") return 60;
          if (label === "D") return 120;
          return 120;
        case "open_3_corners":
          if (label === "A") return 60;
          if (label === "B") return 120;
          if (label === "C") return 80;
          if (label === "D") return 60;
          return 120;
        default:
          return 120;
      }
    },

    getSideLabelsForShape(shape) {
      switch (shape) {
        case "straight":
          return ["A"];
        case "l_shape":
          return ["A", "B"];
        case "u_shape":
          return ["A", "B", "C"];
        case "square":
        case "open_3_corners":
          return ["A", "B", "C", "D"];
        default:
          return [];
      }
    },

    // Returns vertices in inches for the polyline representing the plan centerline.
    // Orientation is fixed (normative) to match the typical UI visuals.
    getVerticesForShape({ shape, sideLengthsIn }) {
      const lengths = sideLengthsIn || {};
      const len = (label, fallback) => {
        const v = lengths[label];
        return Number.isFinite(v) ? v : fallback;
      };

      const start = { x: 0, y: 0 };
      const pts = [start];

      const add = (dx, dy) => {
        const last = pts[pts.length - 1];
        pts.push({ x: last.x + dx, y: last.y + dy });
      };

      // NOTE: SVG y axis grows downward.
      // Direction conventions (normative):
      // - Straight: A left→right
      // - L: A down, B right
      // - U: A down, B right, C up
      // - Square: A down, B right, C up, D left
      // - Open (3 corners): A down, B right, C up, D left
      switch (shape) {
        case "straight": {
          add(len("A", Engine.getDefaultSideLengthIn(shape, "A")), 0);
          break;
        }
        case "l_shape": {
          add(0, len("A", Engine.getDefaultSideLengthIn(shape, "A")));
          add(len("B", Engine.getDefaultSideLengthIn(shape, "B")), 0);
          break;
        }
        case "u_shape": {
          add(0, len("A", Engine.getDefaultSideLengthIn(shape, "A")));
          add(len("B", Engine.getDefaultSideLengthIn(shape, "B")), 0);
          add(0, -len("C", Engine.getDefaultSideLengthIn(shape, "C")));
          break;
        }
        case "open_3_corners": {
          add(0, len("A", Engine.getDefaultSideLengthIn(shape, "A")));
          add(len("B", Engine.getDefaultSideLengthIn(shape, "B")), 0);
          add(0, -len("C", Engine.getDefaultSideLengthIn(shape, "C")));
          add(-len("D", Engine.getDefaultSideLengthIn(shape, "D")), 0);
          break;
        }
        case "square": {
          add(0, len("A", Engine.getDefaultSideLengthIn(shape, "A")));
          add(len("B", Engine.getDefaultSideLengthIn(shape, "B")), 0);
          add(0, -len("C", Engine.getDefaultSideLengthIn(shape, "C")));
          add(-len("D", Engine.getDefaultSideLengthIn(shape, "D")), 0);
          // Close loop for visual stability
          pts.push({ x: start.x, y: start.y });
          break;
        }
        default:
          break;
      }

      return pts;
    },

    computeBBox(points) {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const p of points) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
      }
      if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 1, maxY: 1 };
      return { minX, minY, maxX, maxY };
    },

    // Produces a normalized plan model suitable for visualization.
    // Uses effective lengths when available, otherwise raw lengths, otherwise defaults.
    normalizePlan(configurationInput) {
      const shape = configurationInput?.shape || "";
      const sides = Array.isArray(configurationInput?.sides) ? configurationInput.sides : [];
      const openings = Array.isArray(configurationInput?.openings) ? configurationInput.openings : [];
      const edgeDistanceIn = typeof configurationInput?.edgeDistanceIn === "number" && Number.isFinite(configurationInput.edgeDistanceIn) ? configurationInput.edgeDistanceIn : 0;

      const sideLengthsIn = {};
      for (const s of sides) {
        const label = s?.label;
        if (!label) continue;
        const eff = s?.effectiveLengthIn;
        const raw = s?.rawLengthIn;
        if (Number.isFinite(eff)) sideLengthsIn[label] = eff;
        else if (Number.isFinite(raw)) sideLengthsIn[label] = raw;
      }

      const vertices = Engine.getVerticesForShape({ shape, sideLengthsIn });
      const bbox = Engine.computeBBox(vertices);

      // Side segments (label → start/end) with lengths
      const labels = Engine.getSideLabelsForShape(shape);
      const sideSegments = {};
      for (let i = 0; i < labels.length; i++) {
        const label = labels[i];
        // For square, vertices includes closure point; segment indices still map 0..3
        const p0 = vertices[i];
        const p1 = vertices[i + 1];
        if (!p0 || !p1) continue;
        // Find raw length for this side
        const sideData = sides.find((s) => s?.label === label);
        const rawLengthIn = sideData && Number.isFinite(sideData.rawLengthIn) ? sideData.rawLengthIn : null;
        const effectiveLengthIn =
          sideData && Number.isFinite(sideData.effectiveLengthIn) ? sideData.effectiveLengthIn : null;
        sideSegments[label] = { p0, p1, rawLengthIn, effectiveLengthIn };
      }

      // One opening per side (v1). Keep the first opening encountered for a side.
      const openingBySide = {};
      for (let idx = 0; idx < openings.length; idx++) {
        const o = openings[idx];
        const side = o?.side;
        if (!side || openingBySide[side]) continue;
        openingBySide[side] = { ...o, index: idx + 1 };
      }

      return {
        shape,
        vertices,
        bbox,
        sideSegments,
        openingBySide,
        edgeDistanceIn,
      };
    },

    _heightCodeFromHeightIn(heightIn) {
      if (heightIn === 36) return "H34";
      if (heightIn === 42) return "H40";
      if (heightIn === 48) return "H46";
      return "";
    },

    _normalizeCatalog(rawCatalog) {
      const list = (arr) => (Array.isArray(arr) ? arr : []);
      const byHeightStandard = { H34: {}, H40: {}, H46: {} };
      const byHeightPerforated = { H34: {}, H40: {}, H46: {} };

      const isPerforated = (mf) => {
        const holes = mf?.holes;
        if (holes === true) return true;
        if (holes == null) return false;
        const s = String(holes).toLowerCase().trim();
        return s === "true" || s === "1" || s === "yes";
      };

      const addVariant = (v, target) => {
        const mf = v?.metafields || {};
        if (mf.type && mf.type !== "panel") return;
        const height = mf.height_code || "";
        const lengthIn = Number(mf.length_in);
        if (!height || !Number.isFinite(lengthIn)) return;
        if (!target[height]) target[height] = {};
        const bucket = target[height];
        const key = String(lengthIn);
        if (!bucket[key]) bucket[key] = [];
        bucket[key].push(v);
      };

      for (const v of list(rawCatalog?.glassStandard)) {
        const mf = v?.metafields || {};
        if (isPerforated(mf)) addVariant(v, byHeightPerforated);
        else addVariant(v, byHeightStandard);
      }
      for (const v of list(rawCatalog?.glassPerforated)) {
        const mf = v?.metafields || {};
        if (isPerforated(mf)) addVariant(v, byHeightPerforated);
      }

      return { byHeightStandard, byHeightPerforated };
    },

    _list(arr) {
      return Array.isArray(arr) ? arr : [];
    },

    _variantMetaMap(catalog) {
      const map = new Map();
      const add = (v) => {
        const id = v?.variant_id ?? v?.variantId;
        if (!id) return;
        map.set(Number(id), v);
      };
      for (const v of Engine._list(catalog?.glassStandard)) add(v);
      for (const v of Engine._list(catalog?.glassPerforated)) add(v);
      for (const v of Engine._list(catalog?.doors)) add(v);
      for (const v of Engine._list(catalog?.doorKits)) add(v);
      return map;
    },

    /** Maps form finish values to Shopify variant option color values. */
    _getFinishColorValues(finish) {
      const f = String(finish).toLowerCase().trim();
      if (!f) return [];
      // Map form values to Shopify Color option values
      if (f === "stainless") return ["inox", "stainless", "acier"];
      if (f === "matte_black") return ["noir", "matte_black", "black"];
      if (f === "noir") return ["noir", "matte_black", "black"];
      if (f === "inox") return ["inox", "stainless", "acier"];
      return [f];
    },

    _filterByFinish(variants, finish) {
      if (!finish) return variants;
      const colorValues = Engine._getFinishColorValues(finish);
      if (!colorValues.length) return variants;
      
      const matches = variants.filter((v) => {
        // Check variant options (option1, option2, option3) for color
        const option1 = v?.option1 ? String(v.option1).toLowerCase() : "";
        const option2 = v?.option2 ? String(v.option2).toLowerCase() : "";
        const option3 = v?.option3 ? String(v.option3).toLowerCase() : "";
        const allOptions = `${option1} ${option2} ${option3}`.trim();
        
        // Check metafields.detail as fallback
        const detail = v?.metafields?.detail ? String(v.metafields.detail).toLowerCase() : "";
        
        const toCheck = `${allOptions} ${detail}`.trim();
        if (!toCheck) return false;
        
        // Match if any color value is found in options or detail
        return colorValues.some((color) => toCheck.includes(color));
      });
      
      return matches.length ? matches : variants;
    },

    _pickHardwareVariant(variants, predicate, finish) {
      const filtered = Engine._filterByFinish(variants, finish).filter(predicate);
      if (!filtered.length) return null;
      const inStock = filtered.find((v) => v && v.available);
      return inStock || filtered[0] || null;
    },

    _chooseVariant(variantsForLength) {
      if (!Array.isArray(variantsForLength) || variantsForLength.length === 0) return null;
      const inStock = variantsForLength.find((v) => v && v.available);
      return inStock || variantsForLength[0] || null;
    },

    _computeEndGaps(endSum, endGapStartRange, endGapEndRange, tol = 1e-3) {
      const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
      if (!Number.isFinite(endSum)) return null;
      if (!endGapStartRange || !endGapEndRange) return null;
      const minSum = endGapStartRange.min + endGapEndRange.min;
      const maxSum = endGapStartRange.max + endGapEndRange.max;
      if (endSum < minSum - tol || endSum > maxSum + tol) return null;
      let endGapStart = clamp(endSum / 2, endGapStartRange.min, endGapStartRange.max);
      let endGapEnd = endSum - endGapStart;
      if (endGapEnd < endGapEndRange.min) {
        endGapEnd = endGapEndRange.min;
        endGapStart = endSum - endGapEnd;
      } else if (endGapEnd > endGapEndRange.max) {
        endGapEnd = endGapEndRange.max;
        endGapStart = endSum - endGapEnd;
      }
      if (
        endGapStart < endGapStartRange.min - tol ||
        endGapStart > endGapStartRange.max + tol ||
        endGapEnd < endGapEndRange.min - tol ||
        endGapEnd > endGapEndRange.max + tol
      ) {
        return null;
      }
      return { endGapStart, endGapEnd };
    },

    _tryReplaceEndPanelWithPerforated({
      solution,
      effectiveLengthIn,
      endGapStartRange,
      endGapEndRange,
      perforatedLengths,
      target = "end",
    }) {
      if (!solution || !Array.isArray(solution.panels) || solution.panels.length === 0) return null;
      const panels = solution.panels;
      const idx = target === "start" ? 0 : panels.length - 1;
      const oldLen = Number(panels[idx]);
      const gapIn = Number(solution.gapIn) || 0;
      if (!Number.isFinite(oldLen)) return null;
      const perfLens = Array.isArray(perforatedLengths) ? perforatedLengths.filter((v) => Number.isFinite(v)) : [];
      if (perfLens.length === 0) return null;

      const ordered = [...perfLens].sort((a, b) => Math.abs(a - oldLen) - Math.abs(b - oldLen));
      for (const perfLen of ordered) {
        const newPanels = panels.slice();
        newPanels[idx] = perfLen;
        const sumPanels = newPanels.reduce((acc, v) => acc + v, 0);
        const endSum = effectiveLengthIn - sumPanels - (newPanels.length - 1) * gapIn;
        const endGaps = Engine._computeEndGaps(endSum, endGapStartRange, endGapEndRange);
        if (endGaps) {
          return { panels: newPanels, gapIn: solution.gapIn };
        }
      }
      return null;
    },

    _computeGapForPanels({ effectiveLengthIn, endGapStartRange, endGapEndRange, panels, gapMin = 0.75, gapMax = 1.5, tol = 1e-3 }) {
      if (!Array.isArray(panels) || panels.length === 0) return null;
      const n = panels.length;
      const sumPanels = panels.reduce((acc, v) => acc + v, 0);
      const minEndSum = (endGapStartRange?.min ?? 0) + (endGapEndRange?.min ?? 0);
      const maxEndSum = (endGapStartRange?.max ?? 0) + (endGapEndRange?.max ?? 0);
      if (n === 1) {
        const endSum = effectiveLengthIn - sumPanels;
        const endGaps = Engine._computeEndGaps(endSum, endGapStartRange, endGapEndRange, tol);
        return endGaps ? 0 : null;
      }

      const remaining = effectiveLengthIn - sumPanels;
      const gMinAllowed = (remaining - maxEndSum) / (n - 1);
      const gMaxAllowed = (remaining - minEndSum) / (n - 1);
      const gMin = Math.max(gapMin, gMinAllowed);
      const gMax = Math.min(gapMax, gMaxAllowed);
      if (gMin > gMax + tol) return null;
      const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
      const gap = clamp((gMin + gMax) / 2, gMin, gMax);
      const endSum = remaining - (n - 1) * gap;
      const endGaps = Engine._computeEndGaps(endSum, endGapStartRange, endGapEndRange, tol);
      if (!endGaps) return null;
      return gap;
    },

    _trySolveWithRequiredPerforated({
      effectiveLengthIn,
      endGapStartRange,
      endGapEndRange,
      standardLengths,
      perforatedLengths,
      target = "end",
    }) {
      const perfLens = Array.isArray(perforatedLengths) ? perforatedLengths.filter((v) => Number.isFinite(v)) : [];
      if (!perfLens.length || !Number.isFinite(effectiveLengthIn)) return null;
      let best = null;

      // Minimize total panel count; then maximize smallest panel length (avoid tiny panels); then minimize size spread (similar lengths).
      const isBetter = (score, bestScore) => {
        if (!bestScore) return true;
        if (score.count !== bestScore.count) return score.count < bestScore.count;
        if (score.minLen !== bestScore.minLen) return score.minLen > bestScore.minLen;
        return score.sizeSpread < bestScore.sizeSpread;
      };

      const minEndSum = (endGapStartRange?.min ?? 0) + (endGapEndRange?.min ?? 0);
      const maxEndSum = (endGapStartRange?.max ?? 0) + (endGapEndRange?.max ?? 0);
      const tol = 1e-6;

      for (const perfLen of perfLens) {
        const remaining = effectiveLengthIn - perfLen;
        if (remaining < -tol) continue;

        const endSum = effectiveLengthIn - perfLen;
        if (Math.abs(remaining) <= tol) {
          const endGaps = Engine._computeEndGaps(endSum, endGapStartRange, endGapEndRange);
          if (endGaps) {
            return { panels: [perfLen], gapIn: 0 };
          }
          continue;
        }

        if (remaining > tol && remaining >= minEndSum - tol && remaining <= maxEndSum + tol) {
          const endGaps = Engine._computeEndGaps(endSum, endGapStartRange, endGapEndRange);
          if (endGaps) {
            const score = { count: 1, minLen: perfLen, sizeSpread: 0 };
            if (isBetter(score, best?.score)) {
              best = { panels: [perfLen], gapIn: 0, score };
            }
          }
        }

        if (remaining > maxEndSum + tol) {
          const gapMin = 0.75;
          const gapMax = 1.5;
          for (let testGap = gapMin; testGap <= gapMax + 1e-6; testGap += 0.125) {
            const remainingForPanels = remaining - testGap;
            if (remainingForPanels < minEndSum - tol) break;
            const remainingSolution = Engine._solvePanelsForSide({
              effectiveLengthIn: remainingForPanels,
              endGapStartRange,
              endGapEndRange,
              lengths: standardLengths,
              maxDiffRatio: 1,
            });
            if (!remainingSolution) continue;

            const panels = target === "start" ? [perfLen, ...remainingSolution.panels] : [...remainingSolution.panels, perfLen];
            const gap = Engine._computeGapForPanels({
              effectiveLengthIn,
              endGapStartRange,
              endGapEndRange,
              panels,
            });
            if (gap == null) continue;

            const minLen = Math.min(...panels);
            const maxLen = Math.max(...panels);
            const sizeSpread = maxLen - minLen;
            const score = { count: panels.length, minLen, sizeSpread };
            if (isBetter(score, best?.score)) {
              best = { panels, gapIn: gap, score };
            }
          }
        }
      }
      return best ? { panels: best.panels, gapIn: best.gapIn } : null;
    },

    CUSTOM_PERFORATED_MIN_IN: 16,
    CUSTOM_PERFORATED_MAX_IN: 70,
    CUSTOM_PERFORATED_STEP_IN: 1 / 32,
    SMALL_PANEL_MAX_IN: 12,
    CUSTOM_STANDARD_MIN_IN: 12,
    CUSTOM_STANDARD_MAX_IN: 70,
    CUSTOM_STANDARD_MIN_WHEN_REPLACING_SMALL: 4,

    _roundTo32(x) {
      return Math.round(x * 32) / 32;
    },

    _trySolveWithCustomPerforated({
      effectiveLengthIn,
      endGapStartRange,
      endGapEndRange,
      standardLengths,
      standardCandidates,
      requirePerfAtStart = false,
      requirePerfAtEnd = false,
      target = "end",
    }) {
      const minIn = Engine.CUSTOM_PERFORATED_MIN_IN;
      const maxIn = Engine.CUSTOM_PERFORATED_MAX_IN;
      const stepIn = Engine.CUSTOM_PERFORATED_STEP_IN;
      const minEndSum = (endGapStartRange?.min ?? 0) + (endGapEndRange?.min ?? 0);
      const maxEndSum = (endGapStartRange?.max ?? 0) + (endGapEndRange?.max ?? 0);
      const tol = 1e-4;

      const isBetter = (score, bestScore) => {
        if (!bestScore) return true;
        if (score.count !== bestScore.count) return score.count < bestScore.count;
        if (score.minLen !== bestScore.minLen) return score.minLen > bestScore.minLen;
        return score.sizeSpread < bestScore.sizeSpread;
      };

      let best = null;
      const LMin = Math.max(minIn, effectiveLengthIn - maxEndSum - tol);
      const LMax = Math.min(maxIn, effectiveLengthIn - minEndSum + tol);
      if (LMin > LMax + tol) return best;
      for (let L = Math.ceil(LMin / stepIn) * stepIn; L <= LMax + tol; L += stepIn) {
        const Lr = Engine._roundTo32(Math.max(minIn, Math.min(maxIn, L)));
        const remainder = effectiveLengthIn - Lr;
        const onePanelFits = remainder >= minEndSum - tol && remainder <= maxEndSum + tol;
        if (!onePanelFits) {
          const remainingSolution = Engine._solvePanelsForSide({
            effectiveLengthIn: remainder,
            endGapStartRange,
            endGapEndRange,
            lengths: standardLengths,
            maxDiffRatio: 1,
          });
          if (!remainingSolution) continue;
          const standardPanels = remainingSolution.panels;
          const allLengths = target === "start" ? [Lr, ...standardPanels] : [...standardPanels, Lr];
          const gap = Engine._computeGapForPanels({
            effectiveLengthIn,
            endGapStartRange,
            endGapEndRange,
            panels: allLengths,
          });
          if (gap == null) continue;
          const minLen = Math.min(...allLengths);
          const maxLen = Math.max(...allLengths);
          const score = { count: allLengths.length, minLen, sizeSpread: maxLen - minLen };
          if (!isBetter(score, best?.score)) continue;
          const panels = [];
          const standardCands = standardCandidates || {};
          for (let i = 0; i < allLengths.length; i++) {
            const len = allLengths[i];
            const isCustom = (target === "start" && i === 0) || (target === "end" && i === allLengths.length - 1);
            if (isCustom) {
              panels.push({
                lengthIn: Lr,
                variantId: null,
                source: "panel_custom_perforated",
                needsPerforation: true,
                customPerforated: true,
              });
            } else {
              const variants = standardCands[String(len)] || [];
              const chosen = Engine._chooseVariant(variants);
              const variantId = chosen?.variant_id ?? chosen?.variantId ?? null;
              panels.push({
                lengthIn: len,
                variantId,
                source: "panel_standard",
                needsPerforation: false,
              });
            }
          }
          best = {
            solution: { panels: allLengths, gapIn: gap },
            panels,
            score,
          };
        } else {
          const score = { count: 1, minLen: Lr, sizeSpread: 0 };
          if (!isBetter(score, best?.score)) continue;
          best = {
            solution: { panels: [Lr], gapIn: 0 },
            panels: [
              {
                lengthIn: Lr,
                variantId: null,
                source: "panel_custom_perforated",
                needsPerforation: true,
                customPerforated: true,
              },
            ],
            score,
          };
        }
      }
      return best;
    },

    _trySolveWithSingleCustomStandard({ effectiveLengthIn, endGapStartRange, endGapEndRange, minInOverride = null }) {
      const minIn = minInOverride != null ? minInOverride : Engine.CUSTOM_STANDARD_MIN_IN;
      const maxIn = Engine.CUSTOM_STANDARD_MAX_IN;
      const minEndSum = (endGapStartRange?.min ?? 0) + (endGapEndRange?.min ?? 0);
      const maxEndSum = (endGapStartRange?.max ?? 0) + (endGapEndRange?.max ?? 0);
      const tol = 1e-4;
      const LMin = Math.max(minIn, effectiveLengthIn - maxEndSum - tol);
      const LMax = Math.min(maxIn, effectiveLengthIn - minEndSum + tol);
      if (LMin > LMax + tol) return null;
      const L = Engine._roundTo32(Math.max(minIn, Math.min(maxIn, (LMin + LMax) / 2)));
      const endSum = effectiveLengthIn - L;
      const endGaps = Engine._computeEndGaps(endSum, endGapStartRange, endGapEndRange, tol);
      if (!endGaps) return null;
      return {
        solution: { panels: [L], gapIn: 0 },
        panels: [
          {
            lengthIn: L,
            variantId: null,
            source: "panel_custom_standard",
            needsPerforation: false,
          },
        ],
      };
    },

    _resolveSegmentPanels({
      effectiveLengthIn,
      endGapStartRange,
      endGapEndRange,
      standardLengths,
      standardCandidates,
      perforatedCandidates,
      perforatedLengths,
      requirePerfAtStart = false,
      requirePerfAtEnd = false,
      maxDiffRatio = 0.1,
      warnLabel = "",
    }) {
      const warnings = [];
      const requirePerf = !!requirePerfAtStart || !!requirePerfAtEnd;
      const target = requirePerfAtStart ? "start" : "end";

      let solution = Engine._solvePanelsForSide({
        effectiveLengthIn,
        endGapStartRange,
        endGapEndRange,
        lengths: standardLengths,
        maxDiffRatio,
      });

      if (!solution && requirePerf) {
        const forced = Engine._trySolveWithRequiredPerforated({
          effectiveLengthIn,
          endGapStartRange,
          endGapEndRange,
          standardLengths,
          perforatedLengths,
          target,
        });
        if (forced) solution = forced;
      }

      if (!solution) {
        if (requirePerf) {
          const custom = Engine._trySolveWithCustomPerforated({
            effectiveLengthIn,
            endGapStartRange,
            endGapEndRange,
            standardLengths,
            standardCandidates,
            requirePerfAtStart,
            requirePerfAtEnd,
            target,
          });
          if (custom) {
            return { solution: custom.solution, panels: custom.panels, warnings, fallbackUsed: false };
          }
        }
        if (warnLabel) warnings.push(t('cannotResolve', warnLabel));
        return { solution: null, panels: [], warnings };
      }

      if (requirePerf) {
        const forced = Engine._trySolveWithRequiredPerforated({
          effectiveLengthIn,
          endGapStartRange,
          endGapEndRange,
          standardLengths,
          perforatedLengths,
          target,
        });
        const hasPerfLen = solution.panels.some(
          (len) => perforatedCandidates[String(len)] && perforatedCandidates[String(len)].length > 0
        );
        if (forced && (!hasPerfLen || forced.panels.length < solution.panels.length)) {
          solution = forced;
        } else if (!hasPerfLen) {
          const replaced = Engine._tryReplaceEndPanelWithPerforated({
            solution,
            effectiveLengthIn,
            endGapStartRange,
            endGapEndRange,
            perforatedLengths,
            target,
          });
          if (replaced) solution = replaced;
        }
      }

      const smallPanelMaxIn = Engine.SMALL_PANEL_MAX_IN;
      const twoOrMoreSmall =
        solution.panels.length >= 2 && solution.panels.every((len) => Number(len) <= smallPanelMaxIn);
      let panels = [];
      if (twoOrMoreSmall) {
        if (requirePerf) {
          const customPerforated = Engine._trySolveWithCustomPerforated({
            effectiveLengthIn,
            endGapStartRange,
            endGapEndRange,
            standardLengths,
            standardCandidates,
            requirePerfAtStart,
            requirePerfAtEnd,
            target,
          });
          if (customPerforated) {
            return { solution: customPerforated.solution, panels: customPerforated.panels, warnings, fallbackUsed: false };
          }
        } else {
          const customStandard = Engine._trySolveWithSingleCustomStandard({
            effectiveLengthIn,
            endGapStartRange,
            endGapEndRange,
            minInOverride: Engine.CUSTOM_STANDARD_MIN_WHEN_REPLACING_SMALL,
          });
          if (customStandard) {
            solution = customStandard.solution;
            panels = customStandard.panels;
          }
        }
      }
      let fallbackUsed = false;
      if (panels.length === 0) {
        for (let i = 0; i < solution.panels.length; i++) {
          const len = solution.panels[i];
          const isStartPanel = i === 0;
          const isEndPanel = i === solution.panels.length - 1;
          const usePerf = (requirePerfAtStart && isStartPanel) || (requirePerfAtEnd && isEndPanel);
          const hasPerf = perforatedCandidates[String(len)] && perforatedCandidates[String(len)].length > 0;
          if (usePerf && !hasPerf) {
            warnings.push(t('noPerforatedPanels') + (warnLabel ? ` ${warnLabel}` : "") + ".");
            fallbackUsed = true;
            panels.push({
              lengthIn: len,
              variantId: null,
              source: "panel_custom_perforated",
              needsPerforation: true,
              customPerforated: true,
            });
            continue;
          }
          const candidates = usePerf && hasPerf ? perforatedCandidates : standardCandidates;
          const variants = candidates[String(len)] || [];
          const chosen = Engine._chooseVariant(variants);
          const variantId = chosen?.variant_id ?? chosen?.variantId ?? null;
          if (variantId) {
            panels.push({
              lengthIn: len,
              variantId,
              source: usePerf && hasPerf ? "panel_perforated" : "panel_standard",
              needsPerforation: false,
            });
          } else {
            warnings.push(t('panelVariantNotFound', len, usePerf && hasPerf ? "panel_perforated" : "panel_standard"));
          }
        }
      }

      // If we ended up with a tiny stock panel + tiny custom perforated panel on the same segment,
      // prefer a single custom perforated panel that covers the combined length to avoid small junctions.
      if (requirePerf && panels.length === 2) {
        const p1 = panels[0];
        const p2 = panels[1];
        const smallMax = Engine.SMALL_PANEL_MAX_IN;
        const bothSmall =
          Number(p1.lengthIn) <= smallMax &&
          Number(p2.lengthIn) <= smallMax &&
          Number.isFinite(Number(p1.lengthIn)) &&
          Number.isFinite(Number(p2.lengthIn));
        const oneCustomPerf =
          (p1.source === "panel_custom_perforated" && p2.source === "panel_standard") ||
          (p2.source === "panel_custom_perforated" && p1.source === "panel_standard");
        if (bothSmall && oneCustomPerf) {
          const totalLen = Engine._roundTo32(Number(p1.lengthIn) + Number(p2.lengthIn));
          panels = [
            {
              lengthIn: totalLen,
              variantId: null,
              source: "panel_custom_perforated",
              needsPerforation: true,
              customPerforated: true,
            },
          ];
        }
      }

      const isPoorSolution = (pans) => {
        if (!Array.isArray(pans) || pans.length === 0) return false;
        const minLen = Math.min(...pans.map((p) => Number(p.lengthIn)).filter(Number.isFinite));
        return pans.length >= 3 && minLen < 8;
      };
      const solutionIsPoor = (sol) => {
        if (!sol?.panels?.length) return false;
        const lengths = sol.panels.map(Number).filter(Number.isFinite);
        if (lengths.length < 3) return false;
        return Math.min(...lengths) < 8;
      };
      const currentScore = (pans) => {
        const lengths = pans.map((p) => Number(p.lengthIn)).filter(Number.isFinite);
        if (lengths.length === 0) return { count: 0, minLen: 0, sizeSpread: 0 };
        return {
          count: pans.length,
          minLen: Math.min(...lengths),
          sizeSpread: Math.max(...lengths) - Math.min(...lengths),
        };
      };
      const customBetter = (customScore, stockScore) =>
        customScore.count < stockScore.count ||
        (customScore.count === stockScore.count && customScore.minLen > stockScore.minLen) ||
        (customScore.count === stockScore.count && customScore.minLen === stockScore.minLen && customScore.sizeSpread < stockScore.sizeSpread);

      const stockIsPoor = isPoorSolution(panels) || solutionIsPoor(solution);
      const shouldTryCustom = requirePerf && (fallbackUsed || stockIsPoor);
      if (shouldTryCustom) {
        if (fallbackUsed && !stockIsPoor) {
          const retryForced = Engine._trySolveWithRequiredPerforated({
            effectiveLengthIn,
            endGapStartRange,
            endGapEndRange,
            standardLengths,
            perforatedLengths,
            target,
          });
          if (retryForced) {
            const retryPanels = [];
            for (let i = 0; i < retryForced.panels.length; i++) {
              const len = retryForced.panels[i];
              const isStartPanel = i === 0;
              const isEndPanel = i === retryForced.panels.length - 1;
              const usePerf = (requirePerfAtStart && isStartPanel) || (requirePerfAtEnd && isEndPanel);
              const hasPerf = perforatedCandidates[String(len)] && perforatedCandidates[String(len)].length > 0;
              if (usePerf && !hasPerf) {
                retryPanels.push({
                  lengthIn: len,
                  variantId: null,
                  source: "panel_custom_perforated",
                  needsPerforation: true,
                  customPerforated: true,
                });
                continue;
              }
              const variants = usePerf && hasPerf ? perforatedCandidates[String(len)] : standardCandidates[String(len)] || [];
              const chosen = Engine._chooseVariant(variants);
              const variantId = chosen?.variant_id ?? chosen?.variantId ?? null;
              if (variantId) {
                retryPanels.push({
                  lengthIn: len,
                  variantId,
                  source: usePerf && hasPerf ? "panel_perforated" : "panel_standard",
                  needsPerforation: false,
                });
              }
            }
            if (retryPanels.length === retryForced.panels.length) {
              return { solution: retryForced, panels: retryPanels, warnings, fallbackUsed: false };
            }
          }
        }
        const custom = Engine._trySolveWithCustomPerforated({
          effectiveLengthIn,
          endGapStartRange,
          endGapEndRange,
          standardLengths,
          standardCandidates,
          requirePerfAtStart,
          requirePerfAtEnd,
          target,
        });
        if (custom && customBetter(custom.score, currentScore(panels))) {
          const customSum =
            effectiveLengthIn -
            custom.solution.panels.reduce((a, v) => a + v, 0) -
            (custom.solution.panels.length - 1) * (custom.solution.gapIn || 0);
          const customEndGaps = Engine._computeEndGaps(customSum, endGapStartRange, endGapEndRange);
          const customHasNegativeGap =
            customEndGaps && (customEndGaps.endGapStart < 0 || customEndGaps.endGapEnd < 0);
          const singleCustomPanelFits =
            effectiveLengthIn >= 16.25 &&
            custom.solution.panels.length === 1 &&
            Number(custom.solution.panels[0]) >= 16;
          if (customHasNegativeGap && !singleCustomPanelFits) {
            const stockSum =
              effectiveLengthIn -
              solution.panels.reduce((a, v) => a + v, 0) -
              (solution.panels.length - 1) * (solution.gapIn || 0);
            const stockEndGaps = Engine._computeEndGaps(stockSum, endGapStartRange, endGapEndRange);
            const stockAllNonNegative =
              stockEndGaps &&
              stockEndGaps.endGapStart >= 0 &&
              stockEndGaps.endGapEnd >= 0;
            const stockIsAcceptable = stockAllNonNegative && !stockIsPoor;
            if (stockIsAcceptable) {
              return { solution, panels, warnings, fallbackUsed };
            }
          }
          return { solution: custom.solution, panels: custom.panels, warnings, fallbackUsed: false };
        }
      }

      return { solution, panels, warnings, fallbackUsed };
    },

    _buildSegmentSummary({ label, effectiveLengthIn, solution, endGapStartRange, endGapEndRange, panels, doorGapAtStart = null, doorGapAtEnd = null }) {
      const sumPanels = solution.panels.reduce((acc, v) => acc + v, 0);
      const endSum = effectiveLengthIn - sumPanels - (solution.panels.length - 1) * solution.gapIn;
      const endGaps = Engine._computeEndGaps(endSum, endGapStartRange, endGapEndRange);
      return {
        label,
        effectiveLengthIn,
        endGapStart: endGaps ? endGaps.endGapStart : null,
        endGapEnd: endGaps ? endGaps.endGapEnd : null,
        gapIn: solution.gapIn,
        panels,
        doorGapAtStart,
        doorGapAtEnd,
      };
    },

    _solvePanelsForSide({ effectiveLengthIn, endGapStartRange, endGapEndRange, lengths, maxDiffRatio = 0.1 }) {
      const gapMin = 0.75;
      const gapMax = 1.5;
      const tol = 1e-3;
      const sorted = Array.isArray(lengths) ? [...lengths].sort((a, b) => b - a) : [];
      const minLen = sorted.length ? sorted[sorted.length - 1] : 0;
      const minEndSum = (endGapStartRange?.min ?? 0) + (endGapEndRange?.min ?? 0);
      const maxPanels = Math.min(
        50,
        Math.max(6, Math.ceil((effectiveLengthIn - minEndSum) / Math.max(minLen, 1)))
      );
      let best = null;

      const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

      const tryCombo = (combo) => {
        const n = combo.length;
        const sum = combo.reduce((acc, v) => acc + v, 0);
        const minEndSum = (endGapStartRange?.min ?? 0) + (endGapEndRange?.min ?? 0);
        const maxEndSum = (endGapStartRange?.max ?? 0) + (endGapEndRange?.max ?? 0);
        const remaining = effectiveLengthIn - sum;

        const maxLen = Math.max(...combo);
        const minLen = Math.min(...combo);
        const diffRatio = maxLen > 0 ? (maxLen - minLen) / maxLen : 0;
        if (diffRatio > maxDiffRatio + tol) return null;

        if (n === 1) {
          if (remaining < minEndSum - tol || remaining > maxEndSum + tol) return null;
          const endSum = remaining;
          let endGapStart = clamp(endSum / 2, endGapStartRange.min, endGapStartRange.max);
          let endGapEnd = endSum - endGapStart;
          if (endGapEnd < endGapEndRange.min) {
            endGapEnd = endGapEndRange.min;
            endGapStart = endSum - endGapEnd;
          } else if (endGapEnd > endGapEndRange.max) {
            endGapEnd = endGapEndRange.max;
            endGapStart = endSum - endGapEnd;
          }
          if (endGapStart < endGapStartRange.min - tol || endGapStart > endGapStartRange.max + tol) return null;
          return { panels: combo, gapIn: 0, endGapStart, endGapEnd, diffRatio };
        }

        const gMinAllowed = (remaining - maxEndSum) / (n - 1);
        const gMaxAllowed = (remaining - minEndSum) / (n - 1);
        const gMin = Math.max(gapMin, gMinAllowed);
        const gMax = Math.min(gapMax, gMaxAllowed);
        if (gMin > gMax + tol) return null;

        const gap = clamp((gMin + gMax) / 2, gMin, gMax);
        const endSum = remaining - (n - 1) * gap;
        if (endSum < minEndSum - tol || endSum > maxEndSum + tol) return null;

        let endGapStart = clamp(endSum / 2, endGapStartRange.min, endGapStartRange.max);
        let endGapEnd = endSum - endGapStart;
        if (endGapEnd < endGapEndRange.min) {
          endGapEnd = endGapEndRange.min;
          endGapStart = endSum - endGapEnd;
        } else if (endGapEnd > endGapEndRange.max) {
          endGapEnd = endGapEndRange.max;
          endGapStart = endSum - endGapEnd;
        }
        if (
          endGapStart < endGapStartRange.min - tol ||
          endGapStart > endGapStartRange.max + tol ||
          endGapEnd < endGapEndRange.min - tol ||
          endGapEnd > endGapEndRange.max + tol
        ) {
          return null;
        }

        return { panels: combo, gapIn: gap, endGapStart, endGapEnd, diffRatio };
      };

      const evaluate = (combo) => {
        const sol = tryCombo(combo);
        if (!sol) return;
        const avg = sol.panels.reduce((a, b) => a + b, 0) / sol.panels.length;
        const minPanelLen = Math.min(...sol.panels);
        const score = { count: sol.panels.length, minLen: minPanelLen, diffRatio: sol.diffRatio, avg };
        if (
          !best ||
          score.count < best.score.count ||
          (score.count === best.score.count && score.minLen > best.score.minLen) ||
          (score.count === best.score.count && score.minLen === best.score.minLen && score.diffRatio < best.score.diffRatio) ||
          (score.count === best.score.count && score.minLen === best.score.minLen && score.diffRatio === best.score.diffRatio && score.avg > best.score.avg)
        ) {
          best = { ...sol, panels: [...sol.panels], score };
        }
      };

      for (let n = 1; n <= maxPanels; n++) {
        const minSum = effectiveLengthIn - ((endGapStartRange?.max ?? 0) + (endGapEndRange?.max ?? 0)) - gapMax * (n - 1);
        const maxSum = effectiveLengthIn - minEndSum - gapMin * (n - 1);
        if (minSum > maxSum + tol) continue;

        // Single-size panels
        for (const L of sorted) {
          const sum = n * L;
          if (sum >= minSum - tol && sum <= maxSum + tol) {
            evaluate(Array.from({ length: n }, () => L));
          }
        }

        // Two-size panels within 10% diff
        for (let i = 0; i < sorted.length; i++) {
          const L1 = sorted[i];
          for (let j = i; j < sorted.length; j++) {
            const L2 = sorted[j];
            const maxLen = Math.max(L1, L2);
            const minLenPair = Math.min(L1, L2);
            if (maxLen > 0 && (maxLen - minLenPair) / maxLen > 0.1 + tol) continue;
            if (Math.abs(L1 - L2) < tol) continue;
            const delta = L1 - L2;
            const base = n * L2;
            const kMin = Math.ceil((minSum - base) / delta);
            const kMax = Math.floor((maxSum - base) / delta);
            const start = Math.max(0, kMin);
            const end = Math.min(n, kMax);
            if (start > end) continue;
            const k = start;
            const combo = [];
            for (let a = 0; a < k; a++) combo.push(L1);
            for (let b = k; b < n; b++) combo.push(L2);
            evaluate(combo);
          }
        }
      }

      return best ? { panels: best.panels, gapIn: best.gapIn } : null;
    },

    computeRecommendedBom(configurationInput, catalog) {
      const baseValidations = [];

      const heightIn = configurationInput?.base?.heightIn;
      const heightCode = Engine._heightCodeFromHeightIn(heightIn);
      if (!heightCode) baseValidations.push({ level: "warn", message: t('invalidHeight') });

      const baseFinish = String(configurationInput?.base?.finish || "").toLowerCase();
      const wantColor = baseFinish === "matte_black" || baseFinish === "noir" ? "noir" : "inox";

      const normalizedCatalog = Engine._normalizeCatalog(catalog || {});
      const byHeightStandard = normalizedCatalog.byHeightStandard || {};
      const byHeightPerforated = normalizedCatalog.byHeightPerforated || {};
      const candidatesByLength = byHeightStandard[heightCode] || {};
      const availableLengths = Object.keys(candidatesByLength)
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v))
        .sort((a, b) => b - a);

      const openingsBySide = {};
      const openingBySide = {};
      const doorBySide = {};
      const sideOrder = Engine.getSideLabelsForShape(configurationInput?.shape || "");
      const sideIndexMap = new Map(sideOrder.map((l, i) => [l, i]));
      const sideInfoMap = new Map((configurationInput?.sides || []).map((s) => [s.label, s]));
      const perforatedCornerBySide = {};

      for (const o of configurationInput?.openings || []) {
        if (o?.side) {
          openingsBySide[o.side] = true;
          if (!openingBySide[o.side]) openingBySide[o.side] = o;
        }
        if (o?.side && o?.type === "door") doorBySide[o.side] = o;
      }

      // Segment values: read side-prefixed keys (e.g. b1In for side B) with fallback to a1/a2 for compatibility.
      const getOpeningSeg = (o, side, kind) => {
        const key = side ? `${String(side).toLowerCase()}${kind}` : `a${kind}`;
        return o?.[key] ?? o?.[`a${kind}`];
      };

      // Door gap constants (inches)
      const HINGE_GAP_PERFORATED = 0.25;
      const HINGE_GAP_WALL_OR_POTEAU = 1;
      const LATCH_GAP = 0.625;
      const SEG_EPS = 1e-6;
      const LATCH_GAP_TOL = 1e-6;

      // --- Perforated corner hints from door hinge positions ---
      for (const o of configurationInput?.openings || []) {
        if (o?.type !== "door" || !o?.side) continue;
        const sideInfo = sideInfoMap.get(o.side);
        if (!sideInfo) continue;
        const cornerAtStart = !!sideInfo.cornerFlags?.cornerAtStart;
        const cornerAtEnd = !!sideInfo.cornerFlags?.cornerAtEnd;
        const hingeOnA1 = String(o?.hinge || "").endsWith("1");
        const a1Eff = Number(getOpeningSeg(o, o.side, "1EffectiveIn"));
        const a2Eff = Number(getOpeningSeg(o, o.side, "2EffectiveIn"));
        const idx = sideIndexMap.get(o.side);
        if (typeof idx !== "number" || sideOrder.length === 0) continue;

        if (hingeOnA1 && cornerAtStart && Number.isFinite(a1Eff) && a1Eff <= SEG_EPS) {
          const prev = sideOrder[(idx - 1 + sideOrder.length) % sideOrder.length];
          perforatedCornerBySide[prev] = { ...(perforatedCornerBySide[prev] || {}), atEnd: true };
        }
        if (!hingeOnA1 && cornerAtEnd && Number.isFinite(a2Eff) && a2Eff <= SEG_EPS) {
          const next = sideOrder[(idx + 1) % sideOrder.length];
          perforatedCornerBySide[next] = { ...(perforatedCornerBySide[next] || {}), atStart: true };
        }
      }

      const pickCandidates = (usePerforated) =>
        (usePerforated ? byHeightPerforated[heightCode] : byHeightStandard[heightCode]) || {};
      const panelCatalogGlobal =
        heightCode && availableLengths.length
          ? {
              standardLengths: [...availableLengths],
              perforatedCandidates: byHeightPerforated[heightCode] || {},
              perforatedLengths: Object.keys(byHeightPerforated[heightCode] || {})
                .map((v) => Number(v))
                .filter((v) => Number.isFinite(v)),
            }
          : null;

      const wallGapRange = { min: 0.75, max: 1.75 };
      const cornerGapNarrow = { min: 0.75, max: 1.25 };
      const cornerGapWide = { min: -0.25, max: 1.25 };

      const cornerPairs = [];
      for (let i = 0; i < sideOrder.length; i++) {
        const current = sideOrder[i];
        const next = sideOrder[(i + 1) % sideOrder.length];
        const currentInfo = sideInfoMap.get(current);
        const nextInfo = sideInfoMap.get(next);
        if (currentInfo?.cornerFlags?.cornerAtEnd && nextInfo?.cornerFlags?.cornerAtStart) {
          cornerPairs.push({ endSide: current, startSide: next });
        }
      }

      const buildEndGapRangesForMask = (mask) => {
        const rangesBySide = {};
        for (const label of sideOrder) {
          const info = sideInfoMap.get(label);
          const cornerFlags = info?.cornerFlags || { cornerAtStart: false, cornerAtEnd: false };
          rangesBySide[label] = {
            start: cornerFlags.cornerAtStart ? null : wallGapRange,
            end: cornerFlags.cornerAtEnd ? null : wallGapRange,
          };
        }
        for (let i = 0; i < cornerPairs.length; i++) {
          const pair = cornerPairs[i];
          const wideOnStart = ((mask >> i) & 1) === 1;
          const startRange = wideOnStart ? cornerGapWide : cornerGapNarrow;
          const endRange = wideOnStart ? cornerGapNarrow : cornerGapWide;
          if (rangesBySide[pair.startSide]) rangesBySide[pair.startSide].start = startRange;
          if (rangesBySide[pair.endSide]) rangesBySide[pair.endSide].end = endRange;
        }
        for (const label of sideOrder) {
          const r = rangesBySide[label];
          if (r && !r.start) r.start = cornerGapNarrow;
          if (r && !r.end) r.end = cornerGapNarrow;
        }
        return rangesBySide;
      };

      const countUnsolvedSegments = (summary) => {
        const sides = summary?.sides || [];
        let count = 0;
        const eps = 1e-6;
        for (const s of sides) {
          if (Array.isArray(s.segments) && s.segments.length) {
            for (const seg of s.segments) {
              const len = Number(seg?.effectiveLengthIn);
              const panels = Array.isArray(seg?.panels) ? seg.panels : [];
              if (Number.isFinite(len) && len > eps && panels.length === 0) count++;
            }
          } else {
            const len = Number(s?.effectiveLengthIn);
            const panels = Array.isArray(s?.panels) ? s.panels : [];
            if (Number.isFinite(len) && len > eps && panels.length === 0) count++;
          }
        }
        return count;
      };

      const countCustomPanels = (summary) => {
        const sides = summary?.sides || [];
        let count = 0;
        for (const s of sides) {
          if (Array.isArray(s.segments) && s.segments.length) {
            for (const seg of s.segments) {
              const panels = Array.isArray(seg?.panels) ? seg.panels : [];
              for (const p of panels) {
                if (p?.source === "panel_custom_standard" || p?.source === "panel_custom_perforated") count++;
              }
            }
          } else {
            const panels = Array.isArray(s?.panels) ? s.panels : [];
            for (const p of panels) {
              if (p?.source === "panel_custom_standard" || p?.source === "panel_custom_perforated") count++;
            }
          }
        }
        return count;
      };

      const countTotalPanels = (summary) => {
        const sides = summary?.sides || [];
        let count = 0;
        for (const s of sides) {
          if (Array.isArray(s.segments) && s.segments.length) {
            for (const seg of s.segments) {
              const panels = Array.isArray(seg?.panels) ? seg.panels : [];
              count += panels.length;
            }
          } else {
            const panels = Array.isArray(s?.panels) ? s.panels : [];
            count += panels.length;
          }
        }
        return count;
      };

      const runWithGapRanges = (endGapRangesBySide) => {
        const validations = baseValidations.slice();
        const layoutSummary = { sides: [] };
        const recommendedBom = [];

        const pushGapOnlySegment = (segLabel, effectiveLengthIn, doorGapAtStart, doorGapAtEnd) => ({
          label: segLabel,
          effectiveLengthIn,
          endGapStart: null,
          endGapEnd: null,
          gapIn: 0,
          panels: [],
          doorGapAtStart,
          doorGapAtEnd,
        });

        for (const side of configurationInput?.sides || []) {
          const label = side?.label || "";
          const effectiveLengthIn = Number.isFinite(side?.effectiveLengthIn) ? side.effectiveLengthIn : side?.rawLengthIn;
          if (!label || !Number.isFinite(effectiveLengthIn)) continue;

          const cornerFlags = side?.cornerFlags || { cornerAtStart: false, cornerAtEnd: false };
          const gapRanges = endGapRangesBySide?.[label];
          const endGapStartRange = gapRanges?.start || wallGapRange;
          const endGapEndRange = gapRanges?.end || wallGapRange;

        // --- Side with opening (no door) ---
        if (openingsBySide[label] && !doorBySide[label]) {
          const opening = openingBySide[label];
          const a1Eff = Number(getOpeningSeg(opening, label, "1EffectiveIn"));
          const a2Eff = Number(getOpeningSeg(opening, label, "2EffectiveIn"));

          if (!Number.isFinite(a1Eff) || !Number.isFinite(a2Eff)) {
            validations.push({ level: "warn", message: t('invalidOpening', label) });
            layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null, endGapStart: null, endGapEnd: null });
            continue;
          }

          const panelCatalog = panelCatalogGlobal;
          if (!panelCatalog) {
            validations.push({ level: "warn", message: t('noPanelsAvailable', label) });
            layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null });
            continue;
          }
          const { standardLengths, perforatedCandidates, perforatedLengths } = panelCatalog;
          const perfReq = perforatedCornerBySide[label] || {};

          const hasA1 = a1Eff > SEG_EPS;
          const hasA2 = a2Eff > SEG_EPS;
          if (!hasA1 && !hasA2) {
            validations.push({ level: "warn", message: t('invalidOpeningNull', label) });
            layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null, endGapStart: null, endGapEnd: null });
            continue;
          }

          const segments = [];
          const allowWideA1 = !!perfReq.atStart;
          const allowWideA2 = !!perfReq.atEnd;
          const panels = [];
          const standardCandidates = pickCandidates(false);
          if (hasA1) {
            const res = Engine._resolveSegmentPanels({
              effectiveLengthIn: a1Eff,
              endGapStartRange,
              endGapEndRange: { min: 0, max: 0 },
              standardLengths,
              standardCandidates,
              perforatedCandidates,
              perforatedLengths,
              requirePerfAtStart: perfReq.atStart,
              maxDiffRatio: allowWideA1 ? 1 : 0.1,
              warnLabel: `le segment ${label}1 (ouverture)`,
            });
            for (const w of res.warnings) validations.push({ level: "warn", message: w });
            if (!res.solution) {
              layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null, endGapStart: null, endGapEnd: null });
              continue;
            }
            panels.push(...res.panels);
            const seg1 = Engine._buildSegmentSummary({
              label: `${label}1`,
              effectiveLengthIn: a1Eff,
              solution: res.solution,
              endGapStartRange,
              endGapEndRange: { min: 0, max: 0 },
              panels: res.panels,
              doorGapAtEnd: 0,
            });
            segments.push(seg1);
          }

          if (hasA2) {
            const res = Engine._resolveSegmentPanels({
              effectiveLengthIn: a2Eff,
              endGapStartRange: { min: 0, max: 0 },
              endGapEndRange,
              standardLengths,
              standardCandidates,
              perforatedCandidates,
              perforatedLengths,
              requirePerfAtEnd: perfReq.atEnd,
              maxDiffRatio: allowWideA2 ? 1 : 0.1,
              warnLabel: `le segment ${label}2 (ouverture)`,
            });
            for (const w of res.warnings) validations.push({ level: "warn", message: w });
            if (!res.solution) {
              layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null, endGapStart: null, endGapEnd: null });
              continue;
            }
            panels.push(...res.panels);
            const seg2 = Engine._buildSegmentSummary({
              label: `${label}2`,
              effectiveLengthIn: a2Eff,
              solution: res.solution,
              endGapStartRange: { min: 0, max: 0 },
              endGapEndRange,
              panels: res.panels,
              doorGapAtStart: 0,
            });
            segments.push(seg2);
          }

          layoutSummary.sides.push({
            label,
            effectiveLengthIn,
            gapIn: null,
            panels,
            segments,
          });

          for (const p of panels) {
            if (p.source === "panel_custom_perforated") {
              recommendedBom.push({ variantId: null, qty: 1, source: "panel_custom_perforated", lengthIn: p.lengthIn });
              continue;
            }
            if (p.source === "panel_custom_standard") {
              recommendedBom.push({ variantId: null, qty: 1, source: "panel_custom_standard", lengthIn: p.lengthIn });
              continue;
            }
            if (!p.variantId) {
              validations.push({
                level: "warn",
                message: t('panelVariantNotFoundHeight', heightCode, p.lengthIn),
              });
              continue;
            }
            const existing = recommendedBom.find((i) => i.variantId === p.variantId);
            if (existing) existing.qty += 1;
            else recommendedBom.push({ variantId: p.variantId, qty: 1, source: p.source || "panel_standard", lengthIn: p.lengthIn });
          }
          continue;
        }

        if (!heightCode || availableLengths.length === 0) {
          validations.push({ level: "warn", message: `Aucun panneau disponible pour le côté ${label}.` });
          layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null });
          continue;
        }

        // --- Side with door ---
        if (doorBySide[label]) {
          const door = doorBySide[label];
          const doorLen = Number(door?.x1In);
          const a1Eff = Number(getOpeningSeg(door, label, "1EffectiveIn"));
          const a2Eff = Number(getOpeningSeg(door, label, "2EffectiveIn"));

          if (!Number.isFinite(doorLen) || !Number.isFinite(a1Eff) || !Number.isFinite(a2Eff)) {
            validations.push({ level: "warn", message: t('invalidDoor', label) });
            layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null, endGapStart: null, endGapEnd: null });
            continue;
          }

          const hingeOnA1 = String(door?.hinge || "").endsWith("1");
          const hingeSegHasLen = hingeOnA1 ? a1Eff > SEG_EPS : a2Eff > SEG_EPS;
          const hingeGap = hingeSegHasLen ? HINGE_GAP_PERFORATED : HINGE_GAP_WALL_OR_POTEAU;
          const gapAtA1Door = hingeOnA1 ? hingeGap : LATCH_GAP;
          const gapAtA2Door = hingeOnA1 ? LATCH_GAP : hingeGap;
          const hingeAtCorner = hingeOnA1 ? cornerFlags.cornerAtStart : cornerFlags.cornerAtEnd;
          const moveToAdjacent = hingeAtCorner && !hingeSegHasLen;
          const allowPerforatedOnDoorSide = !moveToAdjacent;
          const perforatedOnA1 = allowPerforatedOnDoorSide ? hingeOnA1 : false;

          const panelCatalog = panelCatalogGlobal;
          if (!panelCatalog) {
            validations.push({ level: "warn", message: t('noPanelsAvailable', label) });
            layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null, endGapStart: null, endGapEnd: null });
            continue;
          }
          const { standardLengths, perforatedCandidates, perforatedLengths } = panelCatalog;
          const hasA1 = a1Eff > SEG_EPS;
          const hasA2 = a2Eff > SEG_EPS;
          if (!hasA1 && !hasA2) {
            validations.push({ level: "warn", message: t('invalidDoorNull', label) });
            layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null, endGapStart: null, endGapEnd: null });
            continue;
          }
          const allowWideA1 = allowPerforatedOnDoorSide && perforatedOnA1;
          const allowWideA2 = allowPerforatedOnDoorSide && !perforatedOnA1;

          const panels = [];
          const standardCandidates = pickCandidates(false);
          const segments = [];
          if (hasA1) {
            const a1StartRange = endGapStartRange;
            const res = Engine._resolveSegmentPanels({
              effectiveLengthIn: a1Eff,
              endGapStartRange: a1StartRange,
              endGapEndRange: { min: gapAtA1Door, max: gapAtA1Door },
              standardLengths,
              standardCandidates,
              perforatedCandidates,
              perforatedLengths,
              requirePerfAtEnd: allowPerforatedOnDoorSide && perforatedOnA1,
              maxDiffRatio: allowWideA1 ? 1 : 0.1,
              warnLabel: `le segment ${label}1 (porte)`,
            });
            for (const w of res.warnings) validations.push({ level: "warn", message: w });
            if (!res.solution) {
              const isLatchGapOnlyA1 = a1Eff > 0 && a1Eff <= gapAtA1Door + LATCH_GAP_TOL;
              if (isLatchGapOnlyA1) {
                segments.push(pushGapOnlySegment(`${label}1`, a1Eff, null, gapAtA1Door));
              } else {
                layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null, endGapStart: null, endGapEnd: null });
                continue;
              }
            } else {
              panels.push(...res.panels);
              const seg1 = Engine._buildSegmentSummary({
                label: `${label}1`,
                effectiveLengthIn: a1Eff,
                solution: res.solution,
                endGapStartRange: a1StartRange,
                endGapEndRange: { min: gapAtA1Door, max: gapAtA1Door },
                panels: res.panels,
                doorGapAtEnd: gapAtA1Door,
              });
              segments.push(seg1);
            }
          } else {
            segments.push(pushGapOnlySegment(`${label}1`, a1Eff, null, gapAtA1Door));
          }

          if (hasA2) {
            const a2EndRange = endGapEndRange;
            const res = Engine._resolveSegmentPanels({
              effectiveLengthIn: a2Eff,
              endGapStartRange: { min: gapAtA2Door, max: gapAtA2Door },
              endGapEndRange: a2EndRange,
              standardLengths,
              standardCandidates,
              perforatedCandidates,
              perforatedLengths,
              requirePerfAtStart: allowPerforatedOnDoorSide && !perforatedOnA1,
              maxDiffRatio: allowWideA2 ? 1 : 0.1,
              warnLabel: `le segment ${label}2 (porte)`,
            });
            for (const w of res.warnings) validations.push({ level: "warn", message: w });
            if (!res.solution) {
              const isLatchGapOnly = a2Eff > 0 && a2Eff <= gapAtA2Door + LATCH_GAP_TOL;
              if (isLatchGapOnly) {
                segments.push(pushGapOnlySegment(`${label}2`, a2Eff, gapAtA2Door, null));
              } else {
                layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null, endGapStart: null, endGapEnd: null });
                continue;
              }
            } else {
              panels.push(...res.panels);
              const seg2 = Engine._buildSegmentSummary({
                label: `${label}2`,
                effectiveLengthIn: a2Eff,
                solution: res.solution,
                endGapStartRange: { min: gapAtA2Door, max: gapAtA2Door },
                endGapEndRange: a2EndRange,
                panels: res.panels,
                doorGapAtStart: gapAtA2Door,
              });
              segments.push(seg2);
            }
          } else {
            segments.push(pushGapOnlySegment(`${label}2`, a2Eff, gapAtA2Door, null));
          }

          const DoorKit = typeof window !== "undefined" ? window.CfgDoorKit : null;
          const kit =
            DoorKit && typeof DoorKit.matchDoorKit === "function"
              ? DoorKit.matchDoorKit({
                  kits: Engine._list(catalog?.doorKits),
                  finish: baseFinish,
                  heightCode,
                  lengthIn: doorLen,
                  detail: door?.detail,
                })
              : null;
          const kitDetailKey =
            DoorKit && typeof DoorKit.normalizeDetail === "function"
              ? DoorKit.normalizeDetail(door?.detail)
              : String(door?.detail || "").toLowerCase();

          if (kit && (kit.variant_id || kit.variantId)) {
            const kitId = kit.variant_id || kit.variantId;
            const latchSegHasLen = hingeOnA1 ? hasA2 : hasA1;
            const lineItemProperties =
              DoorKit && typeof DoorKit.buildDoorKitLineItemProperties === "function"
                ? DoorKit.buildDoorKitLineItemProperties({
                    door,
                    cornerFlags,
                    hingeOnA1,
                    hingeSegHasLen,
                    latchSegHasLen,
                    hasLoquet: kitDetailKey !== "verrou_plancher",
                  })
                : {};
            recommendedBom.push({
              variantId: Number(kitId),
              qty: 1,
              source: "door_kit",
              lengthIn: doorLen,
              detail: door?.detail || "",
              lineItemProperties,
            });
          } else if (door?.variantId) {
            recommendedBom.push({
              variantId: Number(door.variantId),
              qty: 1,
              source: "door_glass",
              lengthIn: doorLen,
              detail: door?.detail || "",
            });
          } else {
            validations.push({ level: "warn", message: t('missingDoorVariant', label) });
          }

          const sideLetter = label;
          const seg1Label = `${sideLetter}1`;
          const seg2Label = `${sideLetter}2`;
          const doorSummary = {
            [`${sideLetter.toLowerCase()}x1In`]: doorLen,
            [`${seg1Label.toLowerCase()}In`]: Number(getOpeningSeg(door, label, "1In")),
            [`${seg2Label.toLowerCase()}In`]: Number(getOpeningSeg(door, label, "2In")),
            [`${seg1Label.toLowerCase()}EffectiveIn`]: Number(getOpeningSeg(door, label, "1EffectiveIn")),
            [`${seg2Label.toLowerCase()}EffectiveIn`]: Number(getOpeningSeg(door, label, "2EffectiveIn")),
            detail: door?.detail || "",
            hinge: door?.hinge || "",
            swing: door?.swing || "",
            [`gapAt${seg1Label}Door`]: gapAtA1Door,
            [`gapAt${seg2Label}Door`]: gapAtA2Door,
          };

          layoutSummary.sides.push({
            label,
            effectiveLengthIn,
            gapIn: null,
            panels,
            door: doorSummary,
            segments,
          });

          for (const p of panels) {
            if (p.source === "panel_custom_perforated") {
              recommendedBom.push({ variantId: null, qty: 1, source: "panel_custom_perforated", lengthIn: p.lengthIn });
              continue;
            }
            if (p.source === "panel_custom_standard") {
              recommendedBom.push({ variantId: null, qty: 1, source: "panel_custom_standard", lengthIn: p.lengthIn });
              continue;
            }
            const existing = recommendedBom.find((i) => i.variantId === p.variantId);
            if (existing) existing.qty += 1;
            else recommendedBom.push({ variantId: p.variantId, qty: 1, source: p.source, lengthIn: p.lengthIn });
          }
          continue;
        }

        // --- Plain side (no opening/door): solve panels only ---
        const perfReq = perforatedCornerBySide[label] || {};
        const allowWide = !!perfReq.atStart || !!perfReq.atEnd;
        let solution = Engine._solvePanelsForSide({
          effectiveLengthIn,
          endGapStartRange,
          endGapEndRange,
          lengths: availableLengths,
          maxDiffRatio: allowWide ? 1 : 0.1,
        });

        if (!solution && (perfReq.atStart || perfReq.atEnd)) {
          const target = perfReq.atStart ? "start" : "end";
          const forced = Engine._trySolveWithRequiredPerforated({
            effectiveLengthIn,
            endGapStartRange,
            endGapEndRange,
            standardLengths: availableLengths,
            perforatedLengths: panelCatalogGlobal?.perforatedLengths || [],
            target,
          });
          if (forced) solution = forced;
        }
        if (!solution) {
          validations.push({ level: "warn", message: t('cannotResolveSide', label) });
          layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null });
          continue;
        }

        const panelCatalog = panelCatalogGlobal;
        if (!panelCatalog) {
          validations.push({ level: "warn", message: `Aucun panneau disponible pour le côté ${label}.` });
          layoutSummary.sides.push({ label, effectiveLengthIn, panels: [], gapIn: null });
          continue;
        }
        const { perforatedCandidates, perforatedLengths } = panelCatalog;
        const panels = [];
        if (solution && (perfReq.atStart || perfReq.atEnd)) {
          const target = perfReq.atStart ? "start" : "end";
          const forced = Engine._trySolveWithRequiredPerforated({
            effectiveLengthIn,
            endGapStartRange,
            endGapEndRange,
            standardLengths: availableLengths,
            perforatedLengths,
            target,
          });
          if (forced) {
            solution = forced;
          }
        }
        for (let i = 0; i < solution.panels.length; i++) {
          const len = solution.panels[i];
          const isStartPanel = i === 0;
          const isEndPanel = i === solution.panels.length - 1;
          const usePerf = (perfReq.atStart && isStartPanel) || (perfReq.atEnd && isEndPanel);
          const hasPerf = perforatedCandidates[String(len)] && perforatedCandidates[String(len)].length > 0;
          if (usePerf && (!perforatedCandidates[String(len)] || perforatedCandidates[String(len)].length === 0)) {
            const replaced = Engine._tryReplaceEndPanelWithPerforated({
              solution,
              effectiveLengthIn,
              endGapStartRange,
              endGapEndRange,
              perforatedLengths,
              target: perfReq.atStart && isStartPanel ? "start" : "end",
            });
            if (replaced) {
              solution = replaced;
              i = -1;
              continue;
            }
          }
          if (usePerf && !hasPerf) {
            validations.push({ level: "warn", message: `Aucun panneau troué disponible (coin) côté ${label}.` });
            panels.push({
              lengthIn: len,
              variantId: null,
              source: "panel_custom_perforated",
              needsPerforation: true,
              customPerforated: true,
            });
            continue;
          }
          const variants = usePerf
            ? perforatedCandidates[String(len)] || []
            : candidatesByLength[String(len)] || [];
          const chosen = Engine._chooseVariant(variants);
          const variantId = chosen?.variant_id ?? chosen?.variantId ?? null;
          if (variantId) {
            panels.push({ lengthIn: len, variantId, source: usePerf && hasPerf ? "panel_perforated" : "panel_standard" });
          } else {
            validations.push({
              level: "warn",
              message: t('panelVariantNotFound', len, String(len)),
            });
          }
        }

        const sumPanels = solution.panels.reduce((acc, v) => acc + v, 0);
        const endSum = effectiveLengthIn - sumPanels - (solution.panels.length - 1) * solution.gapIn;
        const endGaps = Engine._computeEndGaps(endSum, endGapStartRange, endGapEndRange);

        layoutSummary.sides.push({
          label,
          effectiveLengthIn,
          endGapStart: endGaps ? endGaps.endGapStart : null,
          endGapEnd: endGaps ? endGaps.endGapEnd : null,
          gapIn: solution.gapIn,
          panels,
        });

        for (const p of panels) {
          if (p.source === "panel_custom_perforated") {
            recommendedBom.push({ variantId: null, qty: 1, source: "panel_custom_perforated", lengthIn: p.lengthIn });
            continue;
          }
          if (p.source === "panel_custom_standard") {
            recommendedBom.push({ variantId: null, qty: 1, source: "panel_custom_standard", lengthIn: p.lengthIn });
            continue;
          }
          if (!p.variantId) {
            validations.push({
              level: "warn",
              message: `Aucun variant panneau trouvé (H ${heightCode}, ${p.lengthIn}").`,
            });
            continue;
          }
          const existing = recommendedBom.find((i) => i.variantId === p.variantId);
          if (existing) existing.qty += 1;
          else recommendedBom.push({ variantId: p.variantId, qty: 1, source: p.source || "panel_standard", lengthIn: p.lengthIn });
        }
      }

      // --- Hardware BOM (spigots + pinces + door hardware / kits) ---
      const finish = configurationInput?.base?.finish || "";
      const variantMap = Engine._variantMetaMap(catalog || {});
      const spigotVariants = Engine._list(catalog?.spigots);
      const pinceVariants = Engine._list(catalog?.pinces);
      const screwVariants = Engine._list(catalog?.screws);

      // Spigots: sum per panel (stock panels from catalog; custom perforated = 2 spigots each)
      let totalSpigots = 0;
      for (const item of recommendedBom) {
        if (!item || !item.qty) continue;
        if (!String(item.source || "").includes("panel")) continue;
        if (item.source === "panel_custom_perforated" || item.source === "panel_custom_standard") {
          totalSpigots += 2 * item.qty;
          continue;
        }
        if (!item.variantId) continue;
        const v = variantMap.get(Number(item.variantId));
        const count = Number(v?.metafields?.spigots_count);
        if (Number.isFinite(count) && count > 0) totalSpigots += count * item.qty;
      }

      if (totalSpigots > 0 && spigotVariants.length) {
        const packOne = Engine._pickHardwareVariant(
          spigotVariants,
          (v) => Number(v?.metafields?.spigots_count) === 1,
          finish
        );
        // If packOne is null, try to get any filtered spigot variant (not just the first unfiltered one)
        const pack = packOne || Engine._filterByFinish(spigotVariants, finish)[0];
        const packCount = Number(pack?.metafields?.spigots_count) || 1;
        const qty = Math.ceil(totalSpigots / packCount);
        if (qty > 0 && (pack?.variant_id || pack?.variantId)) {
          recommendedBom.push({ variantId: pack.variant_id || pack.variantId, qty, source: "spigot" });
        } else {
          validations.push({ level: "warn", message: t('noSpigotVariant') });
        }
      }

      // Screws: 4 per spigot, type depends on surfaceType
      const surfaceType = configurationInput?.base?.surfaceType || "";
      const screwsPerSpigot = 4;
      const screwsQty = totalSpigots * screwsPerSpigot;
      const pickScrewVariantByProductId = (productId) => {
        const pid = Number(productId);
        if (!pid) return null;
        return screwVariants.find((v) => Number(v?.product_id) === pid) || null;
      };
      let screwVariant = null;
      if (surfaceType === "wood_composite") {
        // Prefer specific variant for wood/composite: Vis inox hexagonale #14-14 (inox)
        const preferredVid = 52429043958049;
        screwVariant =
          screwVariants.find(
            (v) =>
              Number(v?.product_id) === 10276191994145 &&
              Number(v?.variant_id ?? v?.variantId) === preferredVid
          ) || pickScrewVariantByProductId(10276191994145);
      } else if (surfaceType === "concrete_pavers") {
        screwVariant = pickScrewVariantByProductId(10276192289057);
      }
      if (screwVariant && screwsQty > 0 && (screwVariant.variant_id || screwVariant.variantId)) {
        const vid = screwVariant.variant_id || screwVariant.variantId;
        recommendedBom.push({ variantId: vid, qty: screwsQty, source: "screw" });
      }

      // Pinces: compute connection counts
      const summaryMap = new Map((layoutSummary.sides || []).map((s) => [s.label, s]));

      const pinceCounts = { wall180: 0, glass180: 0, glass90: 0 };

      const addPanelConnections = (n) => {
        if (n > 1) pinceCounts.glass180 += n - 1;
      };

      for (let i = 0; i < sideOrder.length; i++) {
        const label = sideOrder[i];
        const sideInfo = sideInfoMap.get(label);
        if (!sideInfo) continue;
        const summary = summaryMap.get(label);
        if (!summary) continue;

        if (summary.segments && Array.isArray(summary.segments)) {
          for (const seg of summary.segments) {
            const nPanels = Array.isArray(seg.panels) ? seg.panels.length : 0;
            addPanelConnections(nPanels);
            const isSeg1 = String(seg.label).endsWith("1");
            const isSeg2 = String(seg.label).endsWith("2");
            if (isSeg1) {
              if (seg.doorGapAtStart == null) {
                if (sideInfo.cornerFlags?.cornerAtStart) {
                  // corner counted at end of previous side
                } else {
                  pinceCounts.wall180 += 1;
                }
              }
              if (seg.doorGapAtEnd == null) {
                // door connection, no clamps
              }
            }
            if (isSeg2) {
              if (seg.doorGapAtStart == null) {
                // door connection, no clamps
              }
              if (seg.doorGapAtEnd == null) {
                if (sideInfo.cornerFlags?.cornerAtEnd) {
                  pinceCounts.glass90 += 1;
                } else {
                  pinceCounts.wall180 += 1;
                }
              }
            }
          }
        } else {
          const nPanels = Array.isArray(summary.panels) ? summary.panels.length : 0;
          addPanelConnections(nPanels);
          if (sideInfo.cornerFlags?.cornerAtStart) {
            // corner counted by previous side
          } else if (nPanels > 0) {
            pinceCounts.wall180 += 1;
          }
          if (sideInfo.cornerFlags?.cornerAtEnd) {
            pinceCounts.glass90 += 1;
          } else if (nPanels > 0) {
            pinceCounts.wall180 += 1;
          }
        }
      }

      const pushPince = (mount, angle_deg, count) => {
        if (count <= 0) return;
        const v = Engine._pickHardwareVariant(
          pinceVariants,
          (c) => c?.metafields?.mount === mount && Number(c?.metafields?.angle_deg) === angle_deg,
          finish
        );
        if (v?.variant_id || v?.variantId) {
          recommendedBom.push({ variantId: v.variant_id || v.variantId, qty: count * 1, source: "pince" });
        } else {
          validations.push({ level: "warn", message: t('noPinVariant', mount, angle_deg) });
        }
      };

      pushPince("wall", 180, pinceCounts.wall180);
      pushPince("glass", 180, pinceCounts.glass180);
      pushPince("glass", 90, pinceCounts.glass90);

      // --- Enrich recommendedBom with pricing information ---
      // Create full variant map including all products for price lookup
      const fullVariantMap = new Map(variantMap);
      for (const v of spigotVariants) {
        const id = v?.variant_id ?? v?.variantId;
        if (id) fullVariantMap.set(Number(id), v);
      }
      for (const v of pinceVariants) {
        const id = v?.variant_id ?? v?.variantId;
        if (id) fullVariantMap.set(Number(id), v);
      }
      for (const v of screwVariants) {
        const id = v?.variant_id ?? v?.variantId;
        if (id) fullVariantMap.set(Number(id), v);
      }

      // Add price information to each BOM item
      let totalBomPrice = 0;
      for (const item of recommendedBom) {
        if (!item || !item.variantId) {
          // Custom panels don't have variantId, set price to 0 or null
          item.price = null;
          item.lineTotal = null;
          continue;
        }
        const variant = fullVariantMap.get(Number(item.variantId));
        const price = variant?.price ? Number(variant.price) : null;
        const qty = Number(item.qty) || 0;
        const lineTotal = price !== null && Number.isFinite(price) && qty > 0 ? price * qty : null;
        
        item.price = price;
        item.lineTotal = lineTotal;
        
        if (lineTotal !== null && Number.isFinite(lineTotal)) {
          totalBomPrice += lineTotal;
        }
      }

        return {
          version: "0.1.0",
          validations,
          layoutSummary,
          recommendedBom,
          pricing: {
            totalPrice: totalBomPrice,
          },
        };
      };

      const totalMasks = Math.max(1, 1 << cornerPairs.length);
      let best = null;
      for (let mask = 0; mask < totalMasks; mask++) {
        const gapRanges = buildEndGapRangesForMask(mask);
        const result = runWithGapRanges(gapRanges);
        const warnCount = Array.isArray(result?.validations) ? result.validations.length : 0;
        const unsolvedCount = countUnsolvedSegments(result?.layoutSummary);
        const customPanelCount = countCustomPanels(result?.layoutSummary);
        const totalPanelCount = countTotalPanels(result?.layoutSummary);
        if (
          !best ||
          unsolvedCount < best.unsolvedCount ||
          (unsolvedCount === best.unsolvedCount && warnCount < best.warnCount) ||
          (unsolvedCount === best.unsolvedCount &&
            warnCount === best.warnCount &&
            customPanelCount < best.customPanelCount) ||
          (unsolvedCount === best.unsolvedCount &&
            warnCount === best.warnCount &&
            customPanelCount === best.customPanelCount &&
            totalPanelCount < best.totalPanelCount)
        ) {
          best = { unsolvedCount, warnCount, customPanelCount, totalPanelCount, result };
        }
      }

      return best?.result || runWithGapRanges(buildEndGapRangesForMask(0));
    },
  };

  window.Cfg.Engine = Engine;
})();

